import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Loader2, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";
import { api, getToken, clearToken, formatApiError } from "../lib/api";
import AdminHeader from "../components/admin/AdminHeader";

const slotToBlockRange = (slot) => {
  if (["09:00","10:00","11:00"].includes(slot)) return "9am – 12pm";
  if (["12:00","13:00","14:00"].includes(slot)) return "12pm – 3pm";
  if (["15:00","16:00","17:00"].includes(slot)) return "3pm – 6pm";
  return slot;
};
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";

const STATUS_COLORS = {
  pending: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  confirmed: "bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/40",
  completed: "bg-green-500/10 text-green-300 border-green-500/30",
  cancelled: "bg-red-500/10 text-red-300 border-red-500/30",
};

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  // Gallery moderation state
  const [gallery, setGallery] = useState([]);
  const [galleryTab, setGalleryTab] = useState(false);
  // Brand clips uploader state
  const [brandClips, setBrandClips] = useState([]);
  const [clipsTab, setClipsTab] = useState(false);
  const [clipBrand, setClipBrand] = useState("Tesla");
  const [clipFile, setClipFile] = useState(null);
  const [clipUrlInput, setClipUrlInput] = useState("");
  const [clipUploading, setClipUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, sRes, gRes, cRes] = await Promise.all([
        api.get(`/admin/bookings${filter !== "all" ? `?status=${filter}` : ""}`),
        api.get("/admin/stats"),
        api.get("/admin/gallery"),
        api.get("/admin/brand-clips"),
      ]);
      setBookings(bRes.data);
      setStats(sRes.data);
      setGallery(gRes.data || []);
      setBrandClips(cRes.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const triggerFollowup = async (id) => {
    if (!window.confirm("Send the post-install follow-up email to this customer?")) return;
    try {
      await api.post(`/admin/bookings/${id}/followup`);
      toast.success("Follow-up email queued");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const moderateGallery = async (id, status) => {
    try {
      await api.patch(`/admin/gallery/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteGallery = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addBrandClip = async (e) => {
    e?.preventDefault?.();
    const brand = (clipBrand || "").trim();
    if (!brand) {
      toast.error("Pick a brand first");
      return;
    }
    setClipUploading(true);
    try {
      let mediaUrl = (clipUrlInput || "").trim();
      // If the admin picked a file, upload it first and use the returned URL
      if (clipFile) {
        const fd = new FormData();
        fd.append("file", clipFile);
        const up = await api.post("/admin/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        mediaUrl = up.data.url;
      }
      if (!mediaUrl) {
        toast.error("Pick a file or paste a URL");
        return;
      }
      await api.post("/admin/brand-clips", { brand, media_url: mediaUrl });
      toast.success(`Added to ${brand}`);
      setClipFile(null);
      setClipUrlInput("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClipUploading(false);
    }
  };

  const deleteBrandClip = async (id) => {
    if (!window.confirm("Remove this clip from the brand showcase?")) return;
    try {
      await api.delete(`/admin/brand-clips/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <AdminHeader onRefresh={load} loading={loading} />

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="stats-grid">
          <StatCard label="Total" value={stats.total || 0} accent="white" />
          {STATUSES.map((s) => (
            <StatCard key={s} label={s} value={stats.by_status?.[s] || 0} accent={s} />
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight">All bookings</h2>
            <p className="text-sm text-zinc-500">{bookings.length} {bookings.length === 1 ? "result" : "results"}</p>
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="filter-select" className="w-40 bg-[#0d0d0d] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0d0d0d] border-white/10 text-white">
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-sm border border-white/5 bg-[#0a0a0a] overflow-hidden" data-testid="bookings-table">
          {loading ? (
            <div className="p-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> Loading…</div>
          ) : bookings.length === 0 ? (
            <div className="p-16 text-center text-zinc-500">No bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#080808] text-zinc-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-4 font-medium">Customer</th>
                    <th className="text-left px-5 py-4 font-medium">Service</th>
                    <th className="text-left px-5 py-4 font-medium">Package</th>
                    <th className="text-left px-5 py-4 font-medium">Vehicle</th>
                    <th className="text-left px-5 py-4 font-medium">Date / Time</th>
                    <th className="text-left px-5 py-4 font-medium">Status</th>
                    <th className="text-right px-5 py-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {bookings.map((b) => (
                    <tr key={b.id} className="hover:bg-white/[0.02] transition" data-testid={`booking-row-${b.id}`}>
                      <td className="px-5 py-4">
                        <div className="font-medium text-white">{b.name}</div>
                        <div className="text-xs text-zinc-500">{b.email}</div>
                        <div className="text-xs text-zinc-500">{b.phone}</div>
                      </td>
                      <td className="px-5 py-4 text-zinc-300">{b.service}</td>
                      <td className="px-5 py-4 text-zinc-300" data-testid={`booking-package-${b.id}`}>
                        {b.package ? (
                          <div>
                            <div className="text-white text-sm">{b.package}</div>
                            {b.package_price ? (
                              <div className="text-xs text-zinc-500">£{Number(b.package_price).toFixed(0)}</div>
                            ) : null}
                            {(b.screen_code || b.shooting_stars) && (
                              <div className="text-[10px] text-[#D946EF] mt-0.5">
                                {b.screen_code ? "+ Screen Coded" : ""}{b.screen_code && b.shooting_stars ? " · " : ""}{b.shooting_stars ? "+ Shooting Stars" : ""}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-zinc-300">{b.vehicle}</td>
                      <td className="px-5 py-4">
                        <div className="text-white">{b.booking_date}</div>
                        <div className="text-xs text-zinc-500">{slotToBlockRange(b.time_slot)}</div>
                      </td>
                      <td className="px-5 py-4">
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger data-testid={`status-select-${b.id}`} className={`h-8 px-3 text-xs rounded-full border ${STATUS_COLORS[b.status]} bg-transparent`}>
                            <SelectValue placeholder={b.status} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0d0d0d] border-white/10 text-white">
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-3 justify-end">
                          <button
                            onClick={() => triggerFollowup(b.id)}
                            data-testid={`followup-btn-${b.id}`}
                            className="text-zinc-500 hover:text-[#D946EF] transition text-xs whitespace-nowrap"
                            title="Send post-install feedback / POV request email"
                          >
                            Send follow-up
                          </button>
                          <button
                            onClick={() => remove(b.id)}
                            data-testid={`delete-btn-${b.id}`}
                            className="inline-flex items-center gap-1 text-zinc-500 hover:text-red-400 transition text-xs"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Gallery moderation */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight text-white">Customer Gallery</h2>
              <p className="text-sm text-zinc-500">{gallery.length} submissions · {gallery.filter(g => g.status === "pending").length} pending review</p>
            </div>
            <button onClick={() => setGalleryTab(!galleryTab)} className="text-sm text-[#D946EF] hover:underline" data-testid="toggle-gallery">
              {galleryTab ? "Hide" : "Show"} submissions
            </button>
          </div>
          {galleryTab && (
            <div className="rounded-sm border border-white/5 bg-[#0a0a0a] overflow-hidden" data-testid="gallery-table">
              {gallery.length === 0 ? (
                <div className="p-10 text-center text-zinc-500 text-sm">No submissions yet.</div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {gallery.map((g) => (
                    <li key={g.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`gallery-row-${g.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium">{g.name} <span className="text-zinc-500 font-normal">· {g.email}</span></div>
                        {g.vehicle && <div className="text-xs text-zinc-500 mt-0.5">{g.vehicle}</div>}
                        <a href={g.media_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D946EF] hover:underline break-all">{g.media_url}</a>
                        {g.note && <p className="text-xs text-zinc-400 mt-1">{g.note}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] tracking-wider px-2 py-1 rounded-full border ${g.status === "approved" ? "border-green-500/40 text-green-300" : g.status === "rejected" ? "border-red-500/40 text-red-300" : "border-yellow-500/40 text-yellow-300"}`}>
                          {g.status.toUpperCase()}
                        </span>
                        {g.status !== "approved" && (
                          <button onClick={() => moderateGallery(g.id, "approved")} className="text-xs text-green-300 hover:text-green-200 transition" data-testid={`approve-${g.id}`}>Approve</button>
                        )}
                        {g.status !== "rejected" && (
                          <button onClick={() => moderateGallery(g.id, "rejected")} className="text-xs text-zinc-500 hover:text-yellow-300 transition" data-testid={`reject-${g.id}`}>Reject</button>
                        )}
                        <button onClick={() => deleteGallery(g.id)} className="text-xs text-zinc-500 hover:text-red-400 transition" data-testid={`gallery-delete-${g.id}`}>Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Brand Clips manager */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight text-white">Brand showcase videos</h2>
              <p className="text-sm text-zinc-500">{brandClips.length} clips · upload a video file or paste a URL — appears on the homepage brand tiles.</p>
            </div>
            <button onClick={() => setClipsTab(!clipsTab)} className="text-sm text-[#D946EF] hover:underline" data-testid="toggle-clips">
              {clipsTab ? "Hide" : "Manage"} clips
            </button>
          </div>
          {clipsTab && (
            <div className="rounded-sm border border-white/5 bg-[#0a0a0a] p-5" data-testid="brand-clips-panel">
              <form onSubmit={addBrandClip} className="grid sm:grid-cols-[160px_1fr_auto] gap-3 items-end" data-testid="brand-clip-form">
                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block">Brand</label>
                  <select
                    value={clipBrand}
                    onChange={(e) => setClipBrand(e.target.value)}
                    data-testid="clip-brand-select"
                    className="w-full bg-[#0d0d0d] border border-white/10 rounded-sm px-3 py-2 text-white focus:border-[#D946EF] focus:outline-none"
                  >
                    {["Tesla","Range Rover","Mercedes","BMW","Audi","Volkswagen","Porsche","Ford","Vauxhall"].map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block">Upload a file OR paste a URL</label>
                  <div className="flex gap-2 flex-col sm:flex-row">
                    <input
                      type="file"
                      accept="video/*,image/*"
                      onChange={(e) => setClipFile(e.target.files?.[0] || null)}
                      data-testid="clip-file-input"
                      className="text-xs text-zinc-300 file:mr-3 file:rounded file:border-0 file:bg-[#D946EF] file:text-white file:px-3 file:py-2 file:cursor-pointer file:font-semibold flex-1"
                    />
                    <input
                      type="url"
                      value={clipUrlInput}
                      onChange={(e) => setClipUrlInput(e.target.value)}
                      placeholder="…or paste https://link"
                      data-testid="clip-url-input"
                      className="flex-1 bg-[#0d0d0d] border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:border-[#D946EF] focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={clipUploading || (!clipFile && !clipUrlInput)}
                  data-testid="add-brand-clip-btn"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#D946EF] text-white text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition whitespace-nowrap"
                >
                  {clipUploading ? "Uploading…" : "Add clip"}
                </button>
              </form>

              {brandClips.length === 0 ? (
                <div className="mt-6 text-zinc-500 text-sm">No admin-added clips yet. Upload one above.</div>
              ) : (
                <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="brand-clips-list">
                  {brandClips.map((c) => (
                    <li key={c.id} className="rounded-sm border border-white/5 bg-[#0d0d0d] overflow-hidden" data-testid={`brand-clip-${c.id}`}>
                      <div className="aspect-[9/16] bg-black">
                        {/\.(mp4|mov|webm)(\?.*)?$/i.test(c.media_url) || c.media_url.startsWith("/api/files/") ? (
                          <video src={c.media_url} muted loop autoPlay playsInline className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-zinc-500 p-3 text-center break-all">{c.media_url}</div>
                        )}
                      </div>
                      <div className="p-3 flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-white">{c.brand}</span>
                        <button onClick={() => deleteBrandClip(c.id)} className="text-xs text-zinc-500 hover:text-red-400 transition" data-testid={`delete-clip-${c.id}`}>Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const colorMap = {
    white: "text-white",
    pending: "text-yellow-300",
    confirmed: "text-[#D946EF]",
    completed: "text-green-300",
    cancelled: "text-red-300",
  };
  return (
    <div className="p-5 rounded-sm border border-white/5 bg-[#0a0a0a]" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`mt-2 font-display font-bold text-3xl ${colorMap[accent] || "text-white"}`}>{value}</div>
    </div>
  );
}
